import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, Snowflake } from 'lucide-react'
import { PageHeading } from '@/components/layout/PageHeading'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { KeptAtOptionsTab } from '@/components/inventory/storage/temperatures/KeptAtOptionsTab'
import { ProductMappingTab } from '@/components/inventory/storage/temperatures/ProductMappingTab'
import { TemperaturesTab } from '@/components/inventory/storage/temperatures/TemperaturesTab'
import { PERMISSIONS, usePermission } from '@/lib/permissions'
import { temperaturesTab, type TemperaturesTab as Tab } from '@/lib/storageTemperatures'

/**
 * Temperatures (`/inventory/storage-temperatures`), opened from "❄ Temperatures"
 * on the storage list and from every "Manage temperatures" / "Edit conditions"
 * link. A page rather than a dialog, so nothing ever opens a dialog on top of
 * another to get here.
 *
 * The tab lives in `?tab=` (see `storageTemperaturesHref`) so a link lands on
 * the right one. All three stay mounted while the page is open: switching tab
 * must not throw away unsaved edits on another.
 */
export function StorageTemperaturesPage() {
  const { can } = usePermission()
  // The mapping is product data, behind its own permission.
  const canSeeMapping = can(PERMISSIONS.productsView)
  const [params, setParams] = useSearchParams()
  const requested = temperaturesTab(params.get('tab'))
  const tab: Tab = requested === 'mapping' && !canSeeMapping ? 'temperatures' : requested

  const setTab = (t: string) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (t === 'temperatures') next.delete('tab')
        else next.set('tab', t)
        return next
      },
      { replace: true },
    )

  // A `?tab=mapping` this user cannot see is rewritten to the tab shown.
  const hiddenTabRequested = requested !== tab
  useEffect(() => {
    if (hiddenTabRequested) setTab(tab)
    // `setTab` is rebuilt every render; only the request changing matters.
  }, [hiddenTabRequested, tab])

  const panel = 'data-[state=inactive]:hidden'

  return (
    <div className='space-y-3 p-4 md:p-6'>
      <Link to='/inventory?tab=storage' className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'>
        <ChevronLeft className='h-4 w-4' />
        Storage
      </Link>
      <div className='flex min-w-0 items-center gap-3'>
        <Snowflake className='h-5 w-5 shrink-0 text-sky-600' />
        <PageHeading
          title='Temperatures'
          description='What a unit can be kept at, the names the Kept at menu offers, and which temperature each catalog product needs.'
        />
      </div>

      <Tabs value={tab} onValueChange={setTab} className='space-y-3'>
        <TabsList>
          <TabsTrigger value='temperatures'>Temperatures</TabsTrigger>
          <TabsTrigger value='options'>Kept at options</TabsTrigger>
          {canSeeMapping && <TabsTrigger value='mapping'>Product mapping</TabsTrigger>}
        </TabsList>
        <Card>
          <CardContent className='pt-6'>
            <TabsContent value='temperatures' forceMount className={panel}>
              <TemperaturesTab />
            </TabsContent>
            <TabsContent value='options' forceMount className={panel}>
              <KeptAtOptionsTab />
            </TabsContent>
            {canSeeMapping && (
              <TabsContent value='mapping' forceMount className={panel}>
                <ProductMappingTab />
              </TabsContent>
            )}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  )
}
